import type {
  UpsertContactAttemptMutationOptions,
  UpsertContactAttemptPayload,
} from './types';

import { useMutation } from '@tanstack/react-query';

import { VestigasHTTPError } from '@vestigas/data';

import { upsertContactAttempt } from './upsertContactAttempt';

/**
 * React Query mutation hook for creating or updating a contact attempt for a supplier.
 *
 * Does not invalidate the supplier detail query automatically — the caller is
 * responsible for a single invalidation after all related mutations settle.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Data-Exchange/operation/add_contact_attempts_data_exchange_contact_attempt_put
 */
export const useMutationUpsertContactAttempt = (
  options?: UpsertContactAttemptMutationOptions,
) => {
  const { onError, onSettled, ...mutationOptions } = options ?? {};

  return useMutation<void, VestigasHTTPError, UpsertContactAttemptPayload>({
    mutationFn: (payload) => upsertContactAttempt(payload),
    onError(error, variables, mutateResult, context) {
      onError?.(error, variables, mutateResult, context);
    },
    onSettled(data, error, variables, mutateResult, context) {
      onSettled?.(data, error, variables, mutateResult, context);
    },
    throwOnError: false,
    ...mutationOptions,
  });
};
